import { useState } from "react";
import { getDisplayStatus } from "../util";
import StatusHeader from "../kiosk-attractor/StatusHeader";
import MobileSettingsContainer from "../../components/MobileSettingsContainer";
import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/24/solid";
import { swapMobileAttractorSortKeys } from "../../api/new-api";
import { useColors } from "../../provider/colors-provider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const MobileAttractorImageSettingsContainer = ({
  index,
  d,
  getAttractors,
  onDelete,
  onUpdate,
  objectKey,
  showPromotionDisplayName = false,
  showTierRestrictions = false,
  showSwapOrder = false,
  isLast = false,
  swapLoading,
  setSwapLoading,
  attractorsData,
  setAttractorsData,
}: {
  index: any;
  d: any;
  getAttractors: () => void;
  onDelete: (id: string | number) => void;
  onUpdate: (attractor: any) => void;
  objectKey: "mobileBannerId" | "mobileAttractorId";
  showPromotionDisplayName?: boolean;
  showTierRestrictions?: boolean;
  showSwapOrder?: boolean;
  isLast?: boolean;
  swapLoading: boolean;
  setSwapLoading: (arg: boolean) => void;
  attractorsData: any[];
  setAttractorsData: (data: any) => void;
}) => {
  const { primary } = useColors();
  const [openSettings, setOpenSettings] = useState(false);

  const currentTime = Date.now();

  const attractorWithStatus = getDisplayStatus(d, currentTime);

  const handleDelete = async (id: string | number) => {
    // add a timeout to prevent the modal from closing before the delete is complete
    await onDelete(id);
    setTimeout(() => {
      getAttractors();
    }, 500);

    setOpenSettings(false);
  };

  const handleUpdate = async (attractor: any) => {
    await onUpdate(attractor);

    // Add 1-second delay before fetching attractors
    await new Promise((resolve) => setTimeout(resolve, 1000));

    await getAttractors();
    setOpenSettings(false);
  };

  const handleSwapOrder = async (attractor: any, direction: "up" | "down") => {
    setSwapLoading(true);

    try {
      const currentIndex = attractorsData.findIndex(
        (a) => a.mobileAttractorId === attractor.mobileAttractorId
      );

      // Find the adjacent attractor to swap with
      let adjacentIndex: number;
      if (direction === "up") {
        adjacentIndex = currentIndex - 1;
      } else {
        adjacentIndex = currentIndex + 1;
      }

      // Check if adjacent index is valid
      if (adjacentIndex < 0 || adjacentIndex >= attractorsData.length) {
        setSwapLoading(false);
        return; // Can't move beyond boundaries
      }

      const adjacentAttractor = attractorsData[adjacentIndex];
      const currentSortKey = attractor.sortKey;
      const swappingSortKey = adjacentAttractor.sortKey;

      // Swap the sort keys

      await swapMobileAttractorSortKeys(currentSortKey, swappingSortKey);

      // Update the local state to reflect the swap
      setAttractorsData((prevData: any) => {
        const newData = prevData.map((data: any) => {
          if (data.sortKey === currentSortKey) {
            return { ...data, sortKey: swappingSortKey };
          } else if (data.sortKey === swappingSortKey) {
            return { ...data, sortKey: currentSortKey };
          }
          return data;
        });
        return newData;
      });
    } catch (err) {
      console.log(err);
    } finally {
      setOpenSettings(false);
      setSwapLoading(false);
    }
  };

  return (
    <div className="max-w-sm bg-white border border-gray-200 rounded-2xl shadow dark:bg-gray-800 dark:border-gray-700">
      {showSwapOrder && !swapLoading && (
        <div>
          <div
            className={`flex flex-row justify-center p-2 ${
              index === 0 || isLast ? "justify-center" : "justify-between"
            }`}
          >
            {index !== 0 && (
              <ArrowLeftCircleIcon
                onClick={() => {
                  handleSwapOrder(d, "up");
                }}
                className="w-8 h-8 hover:opacity-60"
                color="blue"
              />
            )}
            {!isLast && (
              <ArrowRightCircleIcon
                onClick={() => {
                  handleSwapOrder(d, "down");
                }}
                className="w-8 h-8 hover:opacity-60"
                color="blue"
              />
            )}
          </div>
        </div>
      )}

      <p className="mb-3">
        <StatusHeader
          displayStatus={attractorWithStatus.displayStatus}
          dayDifference={attractorWithStatus.dayDifference}
        />
      </p>
      <div className="p-5">
        <div className="mb-2">
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            DISPLAY TEXT:
          </p>
          <span className="font-light text-sm">{d.displayText}</span>
        </div>
        <div>
          <p className="mb-2 text-sm font-bold text-gray-900 dark:text-white">
            THUMBNAIL:
          </p>
          <img
            className="shadow-md w-max h-20 object-contain rounded-lg"
            src={d?.thumbnailS3Key}
            alt={`thumbnail-image`}
          />
        </div>
        {d.attractorType === "WithQueryParams" ? (
          <div className="mt-2">
            <p className="text-sm font-bold my-2">CUSTOM INTEGRATION:</p>
            {d.externalLinkUrl ? (
              <p className="mt-2 font-light text-sm">
                *This integration has been successfully setup by Player Elite
                Staff
                <CheckCircleIcon
                  color="success"
                  fontSize="medium"
                  style={{ marginLeft: 3 }}
                />
              </p>
            ) : (
              <>
                <p className="mt-2 font-light text-sm">
                  *After creating this promotion, please email details of your
                  Promotion & custom URL to tickets@playerelite.com.au
                </p>{" "}
                <p className="mt-2 font-light text-sm">
                  {" "}
                  This promotion will not be visible inside your app until
                  processed by Player Elite. Thank you.
                </p>
              </>
            )}
          </div>
        ) : d?.externalLinkUrl !== "" && d?.externalLinkUrl?.length > 0 ? (
          <div className="mt-2 flex flex-col">
            <p className="text-sm font-bold my-2">LINK TO WEBSITE:</p>
            <a
              href={d?.externalLinkUrl}
              className="text-blue-900 italic underline"
              target="_blank"
            >
              {d?.externalLinkUrl}
            </a>
            <p className="mt-2 font-light text-sm">
              *Fullscreen images are not displayed when a website link is used
            </p>
          </div>
        ) : d &&
          d?.fullscreenS3Key &&
          (d?.fullscreenS3Key.includes("img") ||
            d?.fullscreenS3Key.includes("png") ||
            d?.fullscreenS3Key.includes("jpg") ||
            d?.fullscreenS3Key.includes("_full")) ? (
          <div className="mt-2">
            <p className="mb-2 text-sm font-bold text-gray-900 dark:text-white">
              FULLSCREEN:
            </p>
            <img
              className="shadow-md rounded-lg"
              src={d?.fullscreenS3Key}
              alt={`fullscreen-image`}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="p-5">
        {showTierRestrictions && d?.tierLevelText ? (
          <p className="mb-3 p-2 border-primary-900 border-2 rounded-full text-center uppercase text-xs font-bold text-gray-700 dark:text-gray-400">
            Showing to {d?.tierLevelText} users
          </p>
        ) : showTierRestrictions ? (
          <p className="mb-3 p-2 border-primary-900 border-2 rounded-full text-center uppercase text-xs font-bold text-gray-700 dark:text-gray-400">
            Showing to all users
          </p>
        ) : null}
        {openSettings ? (
          <MobileSettingsContainer
            openSettings={openSettings}
            setOpenSettings={setOpenSettings}
            data={d}
            hideCloseButton={false}
            hideDeleteButton={false}
            handleSubmit={handleUpdate}
            handleDelete={handleDelete}
            objectKey={objectKey}
            showPromotionDisplayName={showPromotionDisplayName}
            showTierRestrictions={showTierRestrictions}
          />
        ) : (
          <p
            onClick={() => {
              setOpenSettings(!openSettings);
            }}
            className="text-white hover:opacity-70 hover:cursor-pointer inline-flex items-center px-3 py-2 text-sm font-medium text-center rounded-xl hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300"
            style={{
              background: primary,
            }}
          >
            Settings
            <svg
              className="rtl:rotate-180 w-3.5 h-3.5 ms-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 10"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M1 5h12m0 0L9 1m4 4L9 9"
              />
            </svg>
          </p>
        )}
      </div>
    </div>
  );
};

export default MobileAttractorImageSettingsContainer;
